import { useT } from "@/i18n";
import { PromptSnapshotSchema, SlideResponseSchema } from "@ai-teach/types";
import { useMemo } from "react";
import { ConversationReadOnly } from "./ConversationReadOnly";
import { FreeformCanvasReadOnly } from "./FreeformCanvasReadOnly";
import { HandWritingReadOnly } from "./HandWritingReadOnly";
import { ListenRepeatReadOnly } from "./ListenRepeatReadOnly";
import { PassageReadingReadOnly } from "./PassageReadingReadOnly";
import { EmptyState, ReadOnlyCard } from "./ReadOnlyKit";
import { SoundToSpellingReadOnly } from "./SoundToSpellingReadOnly";
import { SpellingToSoundReadOnly } from "./SpellingToSoundReadOnly";
import { VideoQuestionReadOnly } from "./VideoQuestionReadOnly";
import { VocabularyReadOnly } from "./VocabularyReadOnly";

/** One stored play, as any SlideScore query returns it. JSON columns arrive unparsed. */
export interface SlideScoreRecord {
  slideName: string;
  score?: number | null | undefined;
  promptSnapshot: unknown;
  response: unknown;
  recordingUrls?: unknown;
  imageUrls?: unknown;
}

/**
 * Picks the renderer for one stored play. Both JSON columns are parsed here rather than in each
 * template, so a row written before the current schema degrades to a placeholder instead of
 * throwing — and a retired template, having no renderer, lands in the same place.
 */
export function SlideScoreReadOnly({ record }: { record: SlideScoreRecord }) {
  const t = useT();
  const snapshot = useMemo(() => {
    const parsed = PromptSnapshotSchema.safeParse(record.promptSnapshot);
    return parsed.success ? parsed.data : null;
  }, [record.promptSnapshot]);

  const response = useMemo(() => {
    const parsed = SlideResponseSchema.safeParse(record.response);
    return parsed.success ? parsed.data : null;
  }, [record.response]);

  const recordingUrl = useMemo(() => {
    const urls = (record.recordingUrls ?? {}) as Record<string, string>;
    return (id: string | null | undefined) => (id ? (urls[id] ?? null) : null);
  }, [record.recordingUrls]);

  const imageUrl = useMemo(() => {
    const urls = (record.imageUrls ?? {}) as Record<string, string>;
    return (id: string | null | undefined) => (id ? (urls[id] ?? null) : null);
  }, [record.imageUrls]);

  const score = record.score ?? null;

  if (snapshot && response) {
    const shared = { score, recordingUrl, imageUrl };

    if (snapshot.template === "vocabulary" && response.template === "vocabulary") {
      return <VocabularyReadOnly snapshot={snapshot} response={response} {...shared} />;
    }
    if (snapshot.template === "listenRepeat" && response.template === "listenRepeat") {
      return <ListenRepeatReadOnly snapshot={snapshot} response={response} {...shared} />;
    }
    if (snapshot.template === "spellingToSound" && response.template === "spellingToSound") {
      return <SpellingToSoundReadOnly snapshot={snapshot} response={response} {...shared} />;
    }
    if (snapshot.template === "soundToSpelling" && response.template === "soundToSpelling") {
      return <SoundToSpellingReadOnly snapshot={snapshot} response={response} {...shared} />;
    }
    if (snapshot.template === "passageReading" && response.template === "passageReading") {
      return <PassageReadingReadOnly snapshot={snapshot} response={response} {...shared} />;
    }
    if (snapshot.template === "videoQuestion" && response.template === "videoQuestion") {
      return <VideoQuestionReadOnly snapshot={snapshot} response={response} {...shared} />;
    }
    if (snapshot.template === "conversation" && response.template === "conversation") {
      return <ConversationReadOnly snapshot={snapshot} response={response} {...shared} />;
    }
    if (snapshot.template === "freeformCanvas" && response.template === "freeformCanvas") {
      return <FreeformCanvasReadOnly snapshot={snapshot} response={response} {...shared} />;
    }
    if (snapshot.template === "handWriting" && response.template === "handWriting") {
      return <HandWritingReadOnly snapshot={snapshot} response={response} {...shared} />;
    }
  }

  return (
    <ReadOnlyCard title={record.slideName} score={score}>
      <EmptyState message={t["readonly.unsupported"]} />
    </ReadOnlyCard>
  );
}
